import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import ColorInput from "../../../../common/ColorInput";
import OpeningSlidePreview from "./OpeningSlidePreview";
import Collapse from "../../../../common/Collapse";

export default function OpeningSlideSettings({
  styleSettings,
  handleStyleChange,
}) {
  const { t } = useTranslation();
  const [logoPreview, setLogoPreview] = useState(null);

  const handleRemoveLogo = useCallback(() => {
    handleStyleChange({
      target: {
        name: "openingSlide.logo.url",
        value: null,
      },
    });
  }, [handleStyleChange]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        alert(t("only image files are allowed"));
        return;
      }

      const file = acceptedFiles?.[0];

      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        handleStyleChange({
          target: {
            name: "openingSlide.logo.url",
            value: e.target.result, // base64 string
          },
        });
      };
      reader.readAsDataURL(file);
    },
    [handleStyleChange, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    onDrop,
  });

  const backgroundColor =
    styleSettings?.openingSlide?.backgroundColor || "#ffffff";

  const dropzoneMessage = isDragActive
    ? t("drop logo here")
    : t("drag logo instructions");

  useEffect(() => {
    if (styleSettings?.openingSlide?.logo?.url) {
      if (styleSettings?.openingSlide?.logo?.url.startsWith("http")) {
        fetch(styleSettings?.openingSlide?.logo?.url).then((res) => {
          res.blob().then((blob) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              setLogoPreview(e.target.result);
            };
            reader.readAsDataURL(blob);
          });
        });
      } else {
        setLogoPreview(styleSettings?.openingSlide?.logo?.url);
      }
    } else {
      setLogoPreview(null);
    }
  }, [styleSettings?.openingSlide?.logo?.url]);

  return (
    <Collapse
      title={
        <div className="font-semibold text-gray-800 text-xl">
          {t("opening slide")}
        </div>
      }
      className="mb-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-400">
        <div className="px-4">
          <div className="text-lg font-semibold text-gray-700 mb-2">
            {t("logo")}
          </div>
          <div className="flex flex-col items-stretch py-4 space-y-4">
            <ColorInput
              name="openingSlide.backgroundColor"
              label={t("background color")}
              value={backgroundColor}
              onChange={handleStyleChange}
            />
            <div className="flex flex-col">
              <div
                {...getRootProps({
                  className: `flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    isDragActive
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 bg-white hover:border-blue-400"
                  }`,
                })}
              >
                <input {...getInputProps()} />
                <p className="text-sm text-center text-gray-600">
                  {dropzoneMessage}
                </p>
              </div>
              {logoPreview && (
                <div className="mt-4 flex items-center gap-4">
                  <div className="h-16 w-16 flex items-center justify-center border border-gray-200 rounded-md overflow-hidden bg-white">
                    <img
                      src={logoPreview}
                      alt={t("logo preview")}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">
                      {t("logo preview")}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("logo uploaded")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    {t("remove logo")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-4">
          <div className="text-lg font-semibold text-gray-700 mb-2">
            {t("slide preview")}
          </div>
          <OpeningSlidePreview
            logo={logoPreview}
            backgroundColor={backgroundColor}
          />
        </div>
      </div>
    </Collapse>
  );
}
